<script setup lang="ts">
import { PaginationData } from '@/@types/order'
import { Button } from '@/components/ui/button'
import { Pagination, PaginationEllipsis, PaginationFirst, PaginationLast, PaginationNext, PaginationPrevious } from '@/components/ui/pagination'
import { Link, router, usePage } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'

import { PaginationList, PaginationListItem } from 'reka-ui'
import { inject, ref, watch } from 'vue'

const { pagination, pageParam = 'page', onlyProps = ['orders', 'pagination', 'status', 'id'] } = defineProps<{
    pagination: PaginationData
    pageParam?: string
    onlyProps?: string[]
}>()

const params = inject('params', useUrlSearchParams())
const currentPage = ref(pagination.current_page || 1)

const isAdmin = usePage().url.startsWith('/admin')

function updatePage() {
    router.get(
        isAdmin ? '/admin/orders' : '/orders',
        {
            ...params,
            [pageParam]: currentPage.value,
        },
        {
            only: onlyProps,
            preserveScroll: true,
            preserveState: true,
        },
    )
}

watch(
    () => pagination.current_page,
    (newPage) => {
        currentPage.value = newPage || 1
    },
    {
        flush: 'pre',
    },
)
</script>

<template>
    <Pagination v-model:page="currentPage" :items-per-page="pagination.per_page" :total="pagination.total" :sibling-count="1" show-edges>
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
            <PaginationFirst v-if="items.length >= 7" @click="updatePage" />
            <PaginationPrevious @click="updatePage" />
            <template v-for="(item, index) in items">
                <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
                    <Button
                        class="h-10 w-10 p-0"
                        :as="Link"
                        preserve-scroll
                        preserve-state
                        :data="{
                            ...params,
                            [pageParam]: item.value,
                        }"
                        :only="onlyProps"
                        :href="isAdmin ? '/admin/orders' : '/orders'"
                        :variant="item.value === pagination.current_page ? 'default' : 'outline'"
                    >
                        {{ item.value }}
                    </Button>
                </PaginationListItem>
                <PaginationEllipsis v-else :key="item.type" :index="index" />
            </template>
            <PaginationNext @click="updatePage" />

            <PaginationLast v-if="items.length >= 7" @click="updatePage" />
        </PaginationList>
    </Pagination>
</template>
